import { BlockStack, Button, Divider, Form, InlineStack, Link, Popover, Select, SkeletonBodyText, Text, TextField, Toast } from '@shopify/polaris';
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { useCallback, useState } from 'react';

import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { ENUM_SOCIAL_NAME, useCreateUserSocialProfile, useDeleteUserSocialProfile, useGetUserSocialProfile } from 'queries/user_social_profile.query';

export default function UserSocialProfiles({ userId }: { userId?: string }) {
  const { showDeleteConfirm } = useDeleteConfirm();
  const { data: socialProfiles = [], isLoading } = useGetUserSocialProfile({ user_id: userId });
  const { mutateAsync: createSocialProfile, isPending: creating } = useCreateUserSocialProfile();
  const { mutateAsync: deleteSocialProfile, isPending: deleting } = useDeleteUserSocialProfile();

  const [popoverActive, setPopoverActive] = useState(false);
  const [socialName, setSocialName] = useState<string>(ENUM_SOCIAL_NAME.FACEBOOK);
  const [socialSlug, setSocialSlug] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const socialOptions = Object.values(ENUM_SOCIAL_NAME).map((social) => ({
    label: social.charAt(0).toUpperCase() + social.slice(1),
    value: social,
  }));

  const handleCreate = useCallback(async () => {
    const normalizedSlug = socialSlug.trim();
    if (!userId || !socialName || !normalizedSlug) return;

    try {
      await createSocialProfile({ user_id: userId, social_name: socialName, social_slug: normalizedSlug });
      setSocialSlug('');
      setPopoverActive(false);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Không thể thêm profile mạng xã hội.');
    }
  }, [createSocialProfile, socialName, socialSlug, userId]);

  const handleDelete = useCallback(
    async (profileId: string) => {
      const confirmed = await showDeleteConfirm('Xóa profile mạng xã hội?', 'Profile này sẽ bị xóa khỏi người dùng.');
      if (!confirmed) return;

      try {
        await deleteSocialProfile(profileId);
      } catch (error: any) {
        setErrorMessage(error?.response?.data?.message || error?.message || 'Không thể xóa profile mạng xã hội.');
      }
    },
    [deleteSocialProfile, showDeleteConfirm]
  );

  return (
    <>
      {errorMessage && <Toast error content={errorMessage} onDismiss={() => setErrorMessage('')} />}
      <Divider />
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <Text as="h3" variant="headingMd">
            Profile mạng xã hội
          </Text>
          <Popover
            active={popoverActive}
            preferredPosition="below"
            preferredAlignment="right"
            autofocusTarget="first-node"
            preventCloseOnChildOverlayClick
            activator={
              <Button variant="plain" icon={PlusIcon} onClick={() => setPopoverActive((active) => !active)}>
                Thêm
              </Button>
            }
            onClose={() => setPopoverActive(false)}
          >
            <Popover.Section>
              <div style={{ width: 280 }}>
                <Form onSubmit={handleCreate}>
                  <BlockStack gap="300">
                    <Select label="Mạng xã hội" options={socialOptions} value={socialName} onChange={setSocialName} />
                    <TextField
                      label="Tên hoặc đường dẫn profile"
                      value={socialSlug}
                      onChange={setSocialSlug}
                      placeholder="Ví dụ: user3223 hoặc nguyenvana ..."
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => setSocialSlug('')}
                    />
                    <InlineStack align="end" gap="200">
                      <Button onClick={() => setPopoverActive(false)}>Hủy</Button>
                      <Button variant="primary" submit loading={creating} disabled={!socialSlug.trim()}>
                        Thêm profile
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Form>
              </div>
            </Popover.Section>
          </Popover>
        </InlineStack>

        {isLoading ? (
          <SkeletonBodyText lines={2} />
        ) : socialProfiles.length > 0 ? (
          <BlockStack gap="200">
            {socialProfiles.map((profile) => (
              <InlineStack key={profile.id} align="space-between" blockAlign="center" gap="200" wrap={false}>
                <BlockStack gap="050">
                  <Text as="p" variant="bodySm" fontWeight="semibold">
                    {profile.social_name ? profile.social_name.charAt(0).toUpperCase() + profile.social_name.slice(1) : 'Mạng xã hội'}
                  </Text>
                  {/^https?:\/\//i.test(profile.social_slug || '') ? (
                    <Link url={profile.social_slug} target="_blank" removeUnderline>
                      {profile.social_slug}
                    </Link>
                  ) : (
                    <Text as="p" tone="subdued" breakWord>
                      {profile.social_slug || 'Chưa có thông tin'}
                    </Text>
                  )}
                </BlockStack>
                <Button
                  accessibilityLabel={`Xóa profile ${profile.social_name || ''}`}
                  variant="plain"
                  tone="critical"
                  icon={DeleteIcon}
                  loading={deleting}
                  onClick={() => handleDelete(profile.id)}
                />
              </InlineStack>
            ))}
          </BlockStack>
        ) : (
          <Text as="p" tone="subdued">
            Người dùng chưa có profile mạng xã hội.
          </Text>
        )}
      </BlockStack>
    </>
  );
}

